<template>
  <iProgressBar
    :value="percent"
    :width="barWidth"
    :height="barHeight"
    :border-radius="barBorderRadius"
    :fill-color="foregroundColor"
    :track-color="backgroundColor"
    data-test="progress-bar"
  />
</template>

<script>
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import BorderRadius from "%/dynamic-component-editor/options/border-radius";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import ProgressBarMinimum from "%/dynamic-component-editor/options/progress-bar-minimum";
import ProgressBarMaximum from "%/dynamic-component-editor/options/progress-bar-maximum";
import ProgressBarValue from "%/dynamic-component-editor/options/progress-bar-value";
import ProgressBarForeground from "%/dynamic-component-editor/options/progress-bar-foreground";
import ProgressBarRange from "%/dynamic-component-editor/standard-elements/progress-bar/ProgressBarRange";
import { resolveCompletionTrackColor } from "%/dynamic-component-editor/standard-components/base-story-card/completion-progress-track-color";

const FULL = "100%";
const DEFAULT_THICKNESS = "8px";

export default {
  name: "ProgressBar",
  mixins: [DynamicElementMixin],
  inject: {
    scopedProvidedContexts: { default: null },
  },
  elementPanelSettings: {
    displayName: "Progress Bar",
    displayIcon: "mdi:progress-helper",
    elementType: "element",
    elementGroup: "general",
  },
  options: [
    BackgroundColor,
    ProgressBarForeground,
    ProgressBarMinimum,
    ProgressBarValue,
    ProgressBarMaximum,
  ],
  optionBundles: [
    new AppearanceOptions({
      "default.border-radius": "large",
    }, {
      "border-radius": false,
    }),
    new LayoutWidthHeightOptions({
      "default.layout-width": "fill",
      "default.layout-height": 8,
    }, {
      "max-width": true,
      "max-height": true,
      "min-width": true,
      "min-height": true,
    }),
  ],
  computed: {
    percent() {
      return ProgressBarRange.percentOfRange(
        this.rangeNumber(ProgressBarValue),
        this.rangeNumber(ProgressBarMinimum),
        this.rangeNumber(ProgressBarMaximum),
      );
    },
    barWidth() {
      const value = this.getOptionValue(LayoutWidth);
      if (value === "fill" || value === "hug") {
        return FULL;
      }
      return LayoutWidth.getCssValue(value);
    },
    barHeight() {
      const value = this.getOptionValue(LayoutHeight);
      if (value === "fill") {
        return FULL;
      }
      if (value === "hug") {
        return DEFAULT_THICKNESS;
      }
      return LayoutHeight.getCssValue(value);
    },
    barBorderRadius() {
      return this.getOptionValue(BorderRadius) || "0px";
    },
    foregroundColor() {
      return this.resolveColor(ProgressBarForeground);
    },
    backgroundColor() {
      return resolveCompletionTrackColor(this.resolveColor(BackgroundColor), this.foregroundColor);
    },
  },
  methods: {
    rangeNumber(optionClass) {
      const value = this.getOptionValue(optionClass);
      if (value === undefined && this.editMode) {
        return optionClass.defaultValue;
      }
      return value;
    },
    resolveColor(optionClass) {
      const value = this.getOptionValue(optionClass);
      if (!value || typeof value !== "object") {
        return value;
      }
      return optionClass.convertConfigValueToCompiledValue(value, this.currentDcThemeId, this.themeMode);
    },
  },
};
</script>
